import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import type { DevAgentPendingFile } from "@/stores/devAgentStore.types";
import type { OpenFile } from "../components/MetadataEditorPanel";

interface MetadataEditorState {
  openFiles: OpenFile[];
  activeFileId: string | null;
  acceptedCode: string | null;
  initialContent: Record<string, string>;
  setInitialContent: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setAcceptedCode: (code: string | null) => void;
  openTab: (entry: OpenFile) => void;
  handleCloseMetadataEditor: () => void;
  migrateFileId: (fromId: string, toId: string) => void;
}

interface UseDevAgentEditorBridgeInput {
  editor: MetadataEditorState;
  editorOpen: boolean;
  setEditorOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPendingFile: (file: DevAgentPendingFile | null) => void;
}

export function useDevAgentEditorBridge({
  editor,
  editorOpen,
  setEditorOpen,
  setSidebarCollapsed,
  setPendingFile,
}: UseDevAgentEditorBridgeInput) {
  const { virtualFiles, storeAcceptedCode, pendingFile } = useDevAgentStore(
    useShallow((s) => ({
      virtualFiles: s.virtualFiles,
      storeAcceptedCode: s.acceptedCode,
      pendingFile: s.pendingFile,
    }))
  );
  const [analyzingFile, setAnalyzingFile] = useState<string | null>(null);
  const lastSeededPendingRef = useRef<unknown>(null);

  const openFilesCount = editor.openFiles.length;
  const hasRulesOpen = editor.openFiles.some((file) => file.name === "rules.cls");

  useEffect(() => {
    if (editor.openFiles.length > 0 && !editorOpen) {
      setEditorOpen(true);
    }
  }, [editor.openFiles.length, editorOpen, setEditorOpen]);

  useEffect(() => {
    if (editorOpen && openFilesCount === 0) {
      const timeout = setTimeout(() => {
        if (useDevAgentStore.getState().editorOpen) {
          useDevAgentStore.setState({ pendingFile: null, editorOpen: false });
        }
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [editorOpen, openFilesCount]);

  useEffect(() => {
    if (editorOpen && !hasRulesOpen) {
      setSidebarCollapsed(true);
    }
  }, [editorOpen, hasRulesOpen, setSidebarCollapsed]);

  useEffect(() => {
    if (!storeAcceptedCode) return;
    editor.setAcceptedCode(storeAcceptedCode);
    queueMicrotask(() => useDevAgentStore.getState().setAcceptedCode(null));
  }, [editor, storeAcceptedCode]);

  const mergedInitialContent = useMemo(() => {
    const merged: Record<string, string> = { ...editor.initialContent };
    for (const [id, file] of Object.entries(virtualFiles)) {
      if (!merged[id]) merged[id] = file.content;
    }
    return merged;
  }, [editor.initialContent, virtualFiles]);

  const handleOpenFileInEditor = useCallback(
    (file: { id: string; name: string; metadataType?: string }) => {
      if (file.id.startsWith("art_")) {
        const artifact = useDevAgentStore.getState().artifactsById[file.id];
        if (artifact?.content) {
          setPendingFile({
            id: file.id,
            name: file.name,
            metadataType: file.metadataType,
            content: artifact.content,
          });
          editor.openTab({
            id: file.id,
            name: file.name,
            metadataType: file.metadataType || "ApexClass",
          });
          setEditorOpen(true);
          return;
        }

        void (async () => {
          try {
            const { getMetadataItemById } = await import("../devAgentService");
            const item = await getMetadataItemById(file.id);
            setPendingFile({
              id: file.id,
              name: file.name,
              metadataType: file.metadataType,
              content: item.content || "",
            });
            editor.openTab({
              id: file.id,
              name: file.name,
              metadataType: file.metadataType || "ApexClass",
            });
            setEditorOpen(true);
          } catch (error) {
            console.warn("[artifact-open] fetch failed", error);
          }
        })();
        return;
      }

      editor.openTab({
        id: file.id,
        name: file.name,
        metadataType: file.metadataType || "ApexClass",
      });
      setPendingFile({
        id: file.id,
        name: file.name,
        metadataType: file.metadataType,
      });
      setEditorOpen(true);
    },
    [editor, setEditorOpen, setPendingFile],
  );

  useEffect(() => {
    if (!pendingFile?.content || !pendingFile.id) return;
    if (lastSeededPendingRef.current === pendingFile) return;

    lastSeededPendingRef.current = pendingFile;
    const fileId = String(pendingFile.id);
    editor.setInitialContent((previous) => ({
      ...previous,
      [fileId]: pendingFile.content!,
    }));
    editor.openTab({
      id: fileId,
      name: pendingFile.name,
      metadataType: pendingFile.metadataType || "ApexClass",
    });
    setEditorOpen(true);
  }, [editor, pendingFile, setEditorOpen]);

  const handleOpenCodeChange = useCallback(
    (item: any) => {
      const fileName = item.data.fileName || "untitled";
      const metadataType =
        item.data.metadataType ||
        (item.data.language === "trigger" ? "ApexTrigger" : "ApexClass");
      const fileId = item.data.metadataId || fileName;

      editor.openTab({ id: fileId, name: fileName, metadataType });
      if (item.data.code) editor.setAcceptedCode(item.data.code);
      setPendingFile({ id: fileId, name: fileName, metadataType });
      setEditorOpen(true);
    },
    [editor, setEditorOpen, setPendingFile],
  );

  const handleCloseEditor = useCallback(() => {
    const virtualFilesWithoutRules = {
      ...useDevAgentStore.getState().virtualFiles,
    };
    delete virtualFilesWithoutRules["rules.cls"];

    useDevAgentStore.setState({
      pendingFile: null,
      editorOpen: false,
      virtualFiles: virtualFilesWithoutRules,
    });
    editor.handleCloseMetadataEditor();
  }, [editor]);

  const handleLoadIntoChat = useCallback(
    (payload: {
      id: string;
      name: string;
      metadataType?: string;
      content?: string;
    }) => {
      const fileId = payload.id;
      const content =
        payload.content ||
        editor.initialContent[fileId] ||
        editor.initialContent[payload.name] ||
        useDevAgentStore.getState().virtualFiles[fileId]?.content ||
        "";

      setPendingFile({
        id: fileId,
        name: payload.name,
        metadataType: payload.metadataType,
        content: content || undefined,
      });
    },
    [editor.initialContent, setPendingFile],
  );

  return {
    analyzingFile,
    setAnalyzingFile,
    mergedInitialContent,
    handleOpenFileInEditor,
    handleOpenCodeChange,
    handleCloseEditor,
    handleLoadIntoChat,
  };
}
